// assets/text-handwriting-reveal.js
;(() => {
  class TextHandwritingReveal extends window.BAO.CustomElement() {
    static get requiredElements () {
      return ['container', 'svg']
    }

    constructor () {
      super()
      this.isTicking = false
      this.letterEls = []
      this.onScroll = this.onScroll.bind(this)
      this.updateEl = this.updateEl.bind(this)
    }

    connectedCallback () {
      super.connectedCallback()
      this.text = this.getAttribute('data-text').trim()
      this.fontSize = parseFloat(this.getAttribute('data-font-size')) || 48
      this.textColor = this.getAttribute('data-text-color') || '#000'
      this.startFraction = Math.min(
        0.99,
        Math.max(0, parseFloat(this.getAttribute('data-start')) || 0.8),
      )
      this.endFraction = Math.min(
        1.5,
        Math.max(
          this.startFraction + 0.01,
          parseFloat(this.getAttribute('data-end')) || 1.1,
        ),
      )
      this.buildLetters()
      this.updateEl()
    }

    setupListeners () {
      super.setupListeners()
      this.listeners.add(window, 'scroll', this.onScroll, { passive: true })
      this.listeners.add(window, 'resize', this.onScroll, { passive: true })
    }

    onScroll () {
      if (!this.isTicking) {
        this.isTicking = true
        requestAnimationFrame(this.updateEl)
      }
    }

    buildLetters () {
      const svg = this.els.svg.element
      while (svg.firstChild) svg.removeChild(svg.firstChild)

      const baseY = 60
      const wordSpacing = this.fontSize * 0.5
      let currentX = 60
      const words = this.text.split(/\s+/)
      const ns = 'http://www.w3.org/2000/svg'
      this.letterEls = []

      const widthFor = ch => {
        const fs = this.fontSize
        if (/[.,!?]/.test(ch)) return fs * 0.2
        if (/[ijlt]/.test(ch)) return fs * 0.22
        if (/[frtcesv]/.test(ch)) return fs * 0.3
        if (ch === ' ') return fs * 0.3
        if (/[op]/.test(ch)) return fs * 0.35
        if (/[gy]/.test(ch)) return fs * 0.4
        if (/[uayILhq]/.test(ch)) return fs * 0.45
        if (/[mw]/.test(ch)) return fs * 0.5
        if (/[CFKRSTY]/.test(ch)) return fs * 0.55
        if (/[J]/.test(ch)) return fs * 0.6
        if (/[PU]/.test(ch)) return fs * 0.65
        if (/[BEINXOZ]/.test(ch)) return fs * 0.7
        if (/[HQ]/.test(ch)) return fs * 0.75
        if (/[AGV]/.test(ch)) return fs * 0.8
        if (/[W]/.test(ch)) return fs * 0.85
        if (/[D]/.test(ch)) return fs * 0.9
        if (/[M]/.test(ch)) return fs * 1.1
        return fs * 0.37
      }

      words.forEach((word, wIdx) => {
        for (let i = 0; i < word.length; i += 1) {
          const ch = word[i]
          const t = document.createElementNS(ns, 'text')
          t.setAttribute('x', currentX)
          t.setAttribute('y', baseY)
          t.setAttribute('dominant-baseline', 'middle')
          t.setAttribute('class', 'sec-HandwritingReveal_Letter')
          t.textContent = ch

          t.style.fontFamily = `'Allura', cursive`
          t.style.fontSize = `${this.fontSize}px`
          t.style.fill = 'transparent'
          t.style.stroke = this.textColor
          t.style.strokeWidth = '1.5'
          t.style.strokeLinecap = 'round'
          t.style.strokeLinejoin = 'round'
          t.style.strokeDasharray = '200'
          t.style.strokeDashoffset = '200'
          t.style.opacity = '0'
          t.style.transition = 'opacity 0.2s ease-out'

          svg.appendChild(t)
          this.letterEls.push(t)

          currentX += widthFor(ch)
        }
        if (wIdx < words.length - 1) currentX += wordSpacing
      })

      const totalWidth = Math.max(currentX + 60, 300)
      svg.setAttribute('viewBox', `0 0 ${Math.ceil(totalWidth)} 120`)
      svg.style.width = '100%'
      // height is controlled by CSS (block-size), do not set invalid "auto" attribute
      svg.style.display = 'block'
    }

    updateEl () {
      this.isTicking = false
      const container = this.els.container.element
      const rect = container.getBoundingClientRect()
      const winH = window.innerHeight

      // Raw scroll window (0..1) same as before
      const raw = Math.max(
        0,
        Math.min(1, (winH - rect.top) / (winH + rect.height * 0.1)),
      )
      // Tighter window mapped with later start
      const START = this.startFraction // default 0.80
      const END = this.endFraction // default 1.10
      const progress =
        raw <= START ? 0 : raw >= END ? 1 : (raw - START) / (END - START)
      // Ease-out so the tail finishes smoothly
      const easeOut = t => 1 - Math.pow(1 - t, 3)
      const eased = easeOut(progress)

      const total = this.letterEls.length
      const lettersToAnimate = eased * total

      for (let i = 0; i < total; i += 1) {
        const el = this.letterEls[i]
        const lp = Math.max(0, Math.min(1, lettersToAnimate - i))

        if (lp > 0) {
          el.style.opacity = '1'
          if (lp <= 0.6) {
            const offset = 200 * (1 - lp / 0.6)
            el.style.strokeDashoffset = `${offset}`
            el.style.strokeWidth = '1.5'
            el.style.fill = 'transparent'
            el.style.fillOpacity = '0'
          } else {
            el.style.strokeDashoffset = '0'
            const fillP = (lp - 0.6) / 0.4
            el.style.strokeWidth = `${Math.max(0.3, 1.5 * (1 - fillP))}`
            el.style.fillOpacity = `${Math.min(1, Math.max(0, fillP))}`
            el.style.fill = this.textColor
            el.style.stroke = this.textColor
          }
        } else {
          el.style.opacity = '0'
          el.style.strokeDashoffset = '200'
          el.style.fill = 'transparent'
          el.style.fillOpacity = '0'
          el.style.strokeWidth = '1.5'
        }
      }
    }
  }

  if (!customElements.get('text-handwriting-reveal')) {
    customElements.define('text-handwriting-reveal', TextHandwritingReveal)
  }
})()
